import { Chat } from '../types';
import { chatService } from './chats';

class ChatsCacheService {
    private static readonly STORAGE_KEY = 'chatsCache';
    private cachedChats: Chat[] | null = null;
    private pendingRequest: Promise<Chat[]> | null = null;
    private isLoaded: boolean = false;

    private hydrateFromStorage(): void {
        if (this.cachedChats || this.isLoaded) return;
        try {
            const raw = localStorage.getItem(ChatsCacheService.STORAGE_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw) as any[];
            const revived = parsed.map((chat) => {
                const lastMessage = chat.lastMessage
                    ? {
                        ...chat.lastMessage,
                        timestamp: new Date(chat.lastMessage.timestamp),
                        editedAt: chat.lastMessage.editedAt ? new Date(chat.lastMessage.editedAt) : undefined
                    }
                    : undefined;
                return { ...chat, lastMessage, createdAt: chat.createdAt ? new Date(chat.createdAt) : undefined };
            });
            this.cachedChats = revived;
            this.isLoaded = true;
        } catch (e) {
            console.warn('Failed to hydrate chats cache', e);
        }
    }

    private persist(): void {
        try {
            if (!this.cachedChats) {
                localStorage.removeItem(ChatsCacheService.STORAGE_KEY);
                return;
            }
            localStorage.setItem(ChatsCacheService.STORAGE_KEY, JSON.stringify(this.cachedChats));
        } catch (e) {
            console.warn('Failed to persist chats cache', e);
        }
    }

    /**
     * Загружает чаты. Если уже загружены - возвращает из кэша.
     * @param forceRefresh - если true, игнорирует кэш и загружает заново
     */
    async getChats(forceRefresh: boolean = false): Promise<Chat[]> {
        this.hydrateFromStorage();
        // Если есть кэш и не требуется принудительное обновление
        if (!forceRefresh && this.isLoaded && this.cachedChats) {
            return this.cachedChats;
        }

        // Если уже идёт загрузка, ждём её завершения (дедупликация)
        if (this.pendingRequest) {
            return this.pendingRequest;
        }

        // Начинаем загрузку
        this.pendingRequest = chatService.getChats();

        try {
            const chats = await this.pendingRequest;
            this.cachedChats = chats;
            this.isLoaded = true;
            this.persist();
            return chats;
        } finally {
            this.pendingRequest = null;
        }
    }

    /**
     * Проверяет, загружены ли чаты
     */
    hasChats(): boolean {
        this.hydrateFromStorage();
        return this.isLoaded && this.cachedChats !== null;
    }

    /**
     * Возвращает кэшированные чаты (или пустой массив если не загружены)
     */
    getCachedChats(): Chat[] {
        this.hydrateFromStorage();
        return this.cachedChats || [];
    }

    /**
     * Обновляет конкретный чат в кэше
     */
    updateChat(chatId: string, updater: (chat: Chat) => Chat): void {
        if (!this.cachedChats) return;

        this.cachedChats = this.cachedChats.map(chat =>
            chat.id === chatId ? updater(chat) : chat
        );
        this.persist();
    }

    /**
     * Обновляет чат по telegram_chat_id
     */
    updateChatByTelegramId(telegramChatId: number, updater: (chat: Chat) => Chat): void {
        if (!this.cachedChats) return;

        this.cachedChats = this.cachedChats.map(chat =>
            chat.telegramChatId === telegramChatId ? updater(chat) : chat
        );
        this.persist();
    }

    /**
     * Находит чат по ID
     */
    findChat(chatId: string): Chat | undefined {
        return this.cachedChats?.find(chat => chat.id === chatId);
    }

    /**
     * Находит чат по telegram_chat_id
     */
    findChatByTelegramId(telegramChatId: number): Chat | undefined {
        return this.cachedChats?.find(chat => chat.telegramChatId === telegramChatId);
    }

    /**
     * Сбрасывает кэш (при logout)
     */
    clear(): void {
        this.cachedChats = null;
        this.isLoaded = false;
        this.pendingRequest = null;
        this.persist();
    }

    /**
     * Помечает кэш как устаревший (но не удаляет данные)
     * При следующем вызове getChats без forceRefresh вернёт старые данные,
     * но можно вызвать getChats(true) для принудительной перезагрузки
     */
    invalidate(): void {
        this.isLoaded = false;
    }
}

export const chatsCacheService = new ChatsCacheService();
